import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from 'react';
import { useMemo } from 'react';
import { Box, Card, Grid, Typography } from '@mui/material';
import { createSelector } from '@reduxjs/toolkit';
import { List } from 'immutable';
import { AnalogTemperatureSensor, get_z3k_object_state, WiredTemperatureSensor, Z3KObject, } from 'z3k-config';
import { parseFloatWithComma } from 'utils';
import { getSensorId } from '../utils/getters';
import { boiler_water_sensor_id, climaticOutSideSensorId, dhw_temp_sensor_id, } from '../constants';
import { unpack_validation, } from 'webui/Z3KConfigParser/validation/types';
import { validate_by_specification, validate_list, } from 'webui/Z3KConfigParser/validation/collections';
import { minMax } from 'webui/Z3KConfigParser/validation/basic';
import { DHWControlType } from '../utils/dhw-control-type';
import { HeatingControlType } from './type';
import { NTCSensorRow } from './ntc-sensors/ntc-sensor-row';
export function formatTemperature(value) {
    if (value == null)
        return '';
    if (isNaN(value))
        return '';
    return Math.round(value) + ' °C';
}
const NTCSensorsPage = (props) => {
    const { state, validation, onChangeState, tab_key, volatile_data } = props;
    const ntc_sensors_validation = unpack_validation(validation, 'ntc_sensors');
    const sensors_validation = unpack_validation(ntc_sensors_validation, 'sensors');
    const usedSensorsIds = state.configuration.circuits
        .filter((circ) => circ.control_type !== HeatingControlType.OFF)
        .map((circ) => {
        return getSensorId(state.ntc_sensors.objectArrays, state.ntc_sensors.sensors.map((sens) => sens.id).toArray(), circ.id);
    });
    const ntc_sensors = state.ntc_sensors.sensors
        .map((sensor_settings, i) => [sensor_settings, i])
        .filter(([sens]) => {
        if (sens.id === dhw_temp_sensor_id) {
            const controlType = state.configuration.dhw[0].control_type;
            return (controlType !== DHWControlType.FLOW_DHW &&
                controlType !== DHWControlType.BOILER_PLUS_DHW_BOILER &&
                controlType !== DHWControlType.OFF);
        }
        return (usedSensorsIds.includes(sens.id) ||
            [climaticOutSideSensorId, boiler_water_sensor_id].includes(sens.id));
    })
        .filter(([sens]) => !state.configuration.blackList.includes(sens.id));
    const ntc_sensors_rows = useMemo(() => ntc_sensors.map(([sensor_settings, index]) => {
        const validation = unpack_validation(sensors_validation, index);
        const sensor_state = get_z3k_object_state(volatile_data.z3k_state, sensor_settings.id);
        const temperature = sensor_state != null &&
            sensor_state.sensor_ok &&
            sensor_state.curr_temp != null &&
            formatTemperature(sensor_state.curr_temp -
                sensor_settings.original_shift +
                (parseFloatWithComma(sensor_settings.shift) ?? 0));
        return (_jsx(NTCSensorRow, { index: index, temperature: temperature, name: sensor_settings.name, shift: Number(sensor_settings.shift), sensors: state.ntc_sensors.sensors, onChangeState: onChangeState, tab_key: tab_key, validation: validation }, sensor_settings.id));
    }), [ntc_sensors, sensors_validation, volatile_data]);
    const street_sensors = useMemo(() => ntc_sensors.reduce((acc, [sensor_settings, index]) => {
        const basic_street_sensors = AnalogTemperatureSensor.isOutside(sensor_settings)
            ? sensor_settings.name
            : null;
        const reserve_basic_street_sensors = AnalogTemperatureSensor.isOutsideReserve(sensor_settings)
            ? sensor_settings.name
            : state.ntc_sensors.internet_sensor[0].name;
        return {
            ...acc,
            ...(basic_street_sensors && { street: basic_street_sensors }),
            ...(reserve_basic_street_sensors && {
                reserve: reserve_basic_street_sensors,
            }),
        };
    }, {}), [ntc_sensors]);
    return (_jsxs(_Fragment, { children: [_jsx(Card, { variant: "outlined", children: _jsx(Box, { p: 2, children: _jsxs(Grid, { container: true, spacing: 2, children: [_jsx(Grid, { item: true, xs: 4, children: _jsx(Typography, { children: "\u0414\u0430\u0442\u0447\u0438\u043A" }) }), _jsxs(Grid, { item: true, xs: 4, children: [_jsx(Typography, { children: "\u041F\u043E\u043A\u0430\u0437\u0430\u043D\u0438\u0435" }), _jsx(Typography, { fontStyle: "italic", variant: "caption", color: "textSecondary", children: '(с учётом корректировки)' })] }), _jsx(Grid, { item: true, xs: 4, children: _jsx(Typography, { children: "\u041A\u043E\u0440\u0440\u0435\u043A\u0442\u0438\u0440\u043E\u0432\u043A\u0430" }) }), _jsx(Grid, { item: true, container: true, rowSpacing: 2, children: ntc_sensors_rows })] }) }) }), _jsx(Card, { variant: "outlined", sx: { mt: 1 }, children: _jsxs(Box, { p: 2, children: [_jsx(Typography, { gutterBottom: true, children: "\u0423\u043B\u0438\u0447\u043D\u044B\u0439 \u0434\u0430\u0442\u0447\u0438\u043A: " }), _jsxs(Typography, { gutterBottom: true, children: ["\u041E\u0441\u043D\u043E\u0432\u043D\u043E\u0439: ", street_sensors?.street ?? '-'] }), _jsxs(Typography, { gutterBottom: true, children: ["\u0420\u0435\u0437\u0435\u0440\u0432\u043D\u044B\u0439: ", street_sensors?.reserve ?? '-'] })] }) })] }));
};
export const ntc_sensors_tab = {
    key: 'ntc_sensors',
    title: 'Термодатчики NTC',
    element: NTCSensorsPage,
    state_subset: createSelector((all_tabs_state) => all_tabs_state.ntc_sensors, (all_tabs_state) => all_tabs_state.configuration, (ntc_sensors, configuration) => ({
        ntc_sensors,
        configuration,
    })),
    isShown: (state, volatile_data) => volatile_data.service_mode,
    data2state: ({ device }) => {
        return {
            sensors: List(AnalogTemperatureSensor.get
                .all(device.z3k_config)
                .slice()
                .sort(Z3KObject.cmp_by_id)
                .map((sensor) => {
                const shift = Math.round(sensor.calibration_shift);
                return {
                    id: sensor.id,
                    name: sensor.name,
                    setting_register: sensor.setting_register,
                    original_shift: shift,
                    shift: shift.toString(),
                };
            })),
            internet_sensor: WiredTemperatureSensor.get
                .all(device.z3k_config)
                .slice()
                .sort(Z3KObject.cmp_by_id)
                .map((sensor) => {
                return {
                    id: sensor.id,
                    name: sensor.name,
                    setting_register: sensor.setting_register,
                };
            }),
            objectArrays: device.z3k_config?.object_arrays ?? [],
        };
    },
    validate: validate_by_specification({
        ntc_sensors: validate_by_specification({
            sensors: validate_list(validate_by_specification({
                shift: minMax(-5, 5, 'Допустимый интервал: –5°...+5°'),
            })),
        }),
    }),
    state2data: (state) => (setter) => {
        let z3k_config = setter.z3k_config;
        for (const sensor_setting of state.ntc_sensors.sensors) {
            z3k_config = AnalogTemperatureSensor.get.applySetterById(z3k_config, sensor_setting.id, {
                calibration_shift: Math.round(parseFloatWithComma(sensor_setting.shift)),
            });
        }
        return { ...setter, z3k_config };
    },
};
